import * as _ from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import usePostLoginHandler from './usePostLoginHandler';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { UIActions } from '../ui/uiSlice';
import useNavigate from '../utils/router/useNavigate';

function getActionForError(error: string, platform: string) {
  const displayPlatform = _.capitalize(platform);

  switch (error) {
    case 'missing-integration':
      return UIActions.pushToast({
        level: 'error',
        message: `${displayPlatform} is not integrated`,
      });
    case 'missing-membership': {
      return UIActions.pushToast({
        level: 'error',
        message: `That user account does not belong to the connected ${displayPlatform} account`,
      });
    }
    case 'seat-limit-maxed':
      return UIActions.pushToast({
        level: 'error',
        message:
          'Your team has reached its seat limit. Please upgrade or contact support.',
      });
    case 'user-creation-disallowed':
      return UIActions.pushToast({
        level: 'error',
        message:
          'Oops, your team requires each new member to be invited into Slab. Please ask one of your teammates to invite you.',
      });
    case 'deactivated':
      return UIActions.pushToast({
        level: 'error',
        message: 'This account has been deactivated',
      });
    default:
      return UIActions.pushToast({
        level: 'error',
        message: 'Unknown login error. Please contact support.',
      });
  }
}

interface LoginHandlerProps {
  children: React.ReactNode;
}

const LoginHandler = ({ children }: LoginHandlerProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authToken = searchParams.get('authToken');
  const error = searchParams.get('error');
  const platform = searchParams.get('platform');

  const { pathname } = useLocation();
  const postLoginHandler = usePostLoginHandler();
  const [isCapturingToken, setIsCapturingToken] = useState(false);

  useEffect(() => {
    if (authToken) {
      setIsCapturingToken(true);
      postLoginHandler(authToken);
    } else if (error) {
      const action = getActionForError(error, platform ?? '');
      dispatch(action);
      navigate(pathname, { replace: true });
    }
  }, [
    navigate,
    authToken,
    error,
    platform,
    pathname,
    dispatch,
    postLoginHandler,
  ]);

  return isCapturingToken ? <div>Logging you in...</div> : <>{children}</>;
};

export default LoginHandler;
